import React, { ComponentProps } from 'react';
import Typography from 'antd/es/typography';
import clsx from 'clsx';

import './OpText.scss';

const { Text } = Typography;

interface OpTextProps extends ComponentProps<typeof Text> {
  children?: React.ReactNode | React.ReactNode[];
  inline?: boolean;
  size?: 'extraLarge' | 'large' | 'regular' | 'small' | 'tiny';
  ellipsis?: boolean;
  className?: string;
  fontWeight?: '300' | '400' | '500' | '600' | '700';
  testId?: string;
}

/**
 * OpText is a wrapper around Ant Design's Typography.Text component.
 * It provides styled text with configurable size, weight, and display options.
 *
 * Use this component for displaying text content with consistent typography throughout
 * the application. Supports various sizes, font weights, inline display, and text truncation.
 *
 * @example
 * <OpText>Regular text</OpText>
 * <OpText size="large" fontWeight="600">Bold large text</OpText>
 * <OpText ellipsis>Very long text that will be truncated...</OpText>
 *
 */
export const OpText = ({
  children,
  inline = false,
  size = 'regular',
  ellipsis = false,
  fontWeight = '400',
  testId = 'op-text',
  className,
  ...textProps
}: OpTextProps) => {
  const classes = clsx(
    'op-text',
    `op-text--${fontWeight}`,
    { 'op-text--extra-large': size === 'extraLarge' },
    { 'op-text--large': size === 'large' },
    { 'op-text--regular': size === 'regular' },
    { 'op-text--small': size === 'small' },
    { 'op-text--tiny': size === 'tiny' },
    { 'op-text--inline': inline },
    { 'op-text--ellipsis': ellipsis },
    className,
  );

  return (
    <Text
      className={classes}
      ellipsis={ellipsis}
      data-testid={testId}
      {...textProps}
    >
      {children}
    </Text>
  );
};
